/* Payroll Dashboard — what the month cost, where it went, what is unfinished.
 *
 * Built on components/dashboard, so it matches Grievance and every other module
 * dashboard. One deliberate departure: the 12-month payout series is a chart.js
 * line, not the kit's DistributionBars. Bars compare categories; this is a
 * trend, where the shape between points is the information. That is the line to
 * draw elsewhere too — categories get bars, time gets chart.js.
 *
 * Amounts arrive as plain JSON numbers. The API decrypts and sums them, because
 * every money column in payroll is encrypted at rest and cannot be summed in
 * SQL — see payroll/views/dashboard.py.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    AlertTriangle, CalendarClock, CheckCircle2, Clock, FileWarning, Layers,
    Receipt, TrendingDown, TrendingUp, Users, Wallet,
} from "lucide-react";
import { Line } from "react-chartjs-2";
import {
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Tooltip,
} from "chart.js";
import { DatePicker } from "antd";
import dayjs from "dayjs";

import {
    DashboardSection, DashboardShell, DashboardTable, DistributionBars, SectionGrid,
    StatTile, TileGrid,
} from "../../components/dashboard";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { formatPayrollCurrency } from "../../components/common/CommonFunctions";
import { PAYROLL_DASHBOARD } from "../../constants/api";

// Registered here rather than relying on another dashboard's module side
// effect — this screen can mount without those ever rendering. Re-registering
// the same components is a no-op.
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const isDarkMode = () =>
    typeof document !== "undefined" && document.documentElement.classList.contains("dark");

const RUN_STATUS_LABEL = {
    completed: "Completed",
    pending: "Pending",
    yet_to_publish: "Yet to publish",
};

const RUN_STATUS_TONE = {
    completed: "success",
    pending: "danger",
    yet_to_publish: "warning",
};

/* Deduction buckets keep a fixed colour so the same component reads the same
   way every month, rather than changing hue when its rank changes. */
const DEDUCTION_BAR = {
    pf: "bg-indigo-500",
    esi: "bg-blue-500",
    tds: "bg-orange-500",
    lop: "bg-rose-500",
    advance: "bg-amber-500",
    other: "bg-gray-400",
};

const rupees = (value) => `₹${formatPayrollCurrency(value, 0)}`;

const PayoutTrend = ({ trend, loading }) => {
    const dark = isDarkMode();
    const line = dark ? "hsl(178, 85%, 42%)" : "hsl(178, 96%, 32%)";
    const axis = dark ? "#9ca3af" : "#4b5563";

    const data = useMemo(() => ({
        labels: (trend || []).map((row) => row.label),
        datasets: [{
            label: "Net payout",
            data: (trend || []).map((row) => row.net_payout),
            borderColor: line,
            backgroundColor: dark ? "rgba(20,184,166,0.14)" : "rgba(13,148,136,0.10)",
            pointBackgroundColor: line,
            pointRadius: 3,
            pointHoverRadius: 5,
            borderWidth: 2,
            tension: 0.3,
            fill: true,
        }],
    }), [trend, line, dark]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: "#111827",
                titleColor: "#fff",
                bodyColor: "#fff",
                padding: 10,
                cornerRadius: 8,
                callbacks: {
                    label: (ctx) => ` Net payout: ${rupees(ctx.parsed.y)}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                ticks: { color: axis, font: { size: 11, weight: "500" } },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    color: axis,
                    font: { size: 11 },
                    // Full rupee values would be six digits on every tick and
                    // crowd the axis off the chart.
                    callback: (value) => `₹${Number(value).toLocaleString("en-IN", { notation: "compact" })}`,
                },
                grid: { color: dark ? "rgba(75,85,99,0.2)" : "rgba(229,231,235,0.8)" },
            },
        },
    }), [axis, dark]);

    if (loading) return <Skeleton className="h-64 w-full" />;
    if (!trend?.length) {
        return <p className="text-sm text-muted-foreground">No payroll runs in this window.</p>;
    }

    return (
        <div style={{ height: 280 }}>
            <Line data={data} options={options} />
        </div>
    );
};

const PayrollDashboard = () => {
    const { fetch } = useApiMethods();
    const navigate = useNavigate();

    const [period, setPeriod] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // No period on first load: the API answers with the newest run the
            // caller can actually see, which beats guessing at today's month and
            // opening on an empty screen.
            const params = period
                ? { month: period.month() + 1, year: period.year() }
                : {};
            const res = await fetch(PAYROLL_DASHBOARD, params);
            if (res?.status === "success") setData(res.data);
            else ToastMsg("error", res?.message || "Could not load the payroll dashboard");
        } catch (e) {
            ToastMsg("error", e.message);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    useEffect(() => { load(); }, [load]);

    const tiles = data?.tiles || {};
    const variance = tiles.variance_percent;
    const hasVariance = variance !== null && variance !== undefined;

    const departmentRows = (data?.by_department || []).map((row) => ({
        key: row.key,
        label: row.label,
        count: row.amount,
        employees: row.employees,
    }));

    const deductionRows = (data?.deduction_split || []).map((row) => ({
        key: row.key,
        label: row.label,
        count: row.amount,
    }));

    const attentionColumns = [
        {
            key: "label",
            header: "Run",
            render: (row) => (
                <>
                    <span className="font-semibold text-sidebar-primary">{row.label}</span>
                    {row.run_type !== "regular" && (
                        <span className="ml-2 text-muted-foreground capitalize">{row.run_type}</span>
                    )}
                </>
            ),
        },
        {
            key: "status",
            header: "Status",
            render: (row) => RUN_STATUS_LABEL[row.status] || row.status || "—",
        },
        {
            key: "total_employee",
            header: "Employees",
            className: "py-2 tabular-nums",
            render: (row) => row.total_employee || 0,
        },
    ];

    return (
        <DashboardShell
            Icon={Wallet}
            title="Payroll Dashboard"
            subtitle="Cost, deductions and unfinished runs for the payroll you can see"
            loading={loading}
            hasData={!!data}
            toolbar={
                <DatePicker
                    picker="month"
                    allowClear={false}
                    value={period || (data?.period ? dayjs(`${data.period.year}-${data.period.month}-01`) : null)}
                    onChange={(value) => value && setPeriod(value)}
                    placeholder="Select month"
                />
            }
            actions={
                <Button variant="outline" size="sm" onClick={() => navigate("/payroll/payrollprocess")}>
                    <CalendarClock className="h-4 w-4" /> Open payroll
                </Button>
            }
        >
            <TileGrid>
                <StatTile Icon={Wallet} label="Net payout" value={rupees(tiles.net_payout)} />
                <StatTile Icon={Receipt} label="Deductions" value={rupees(tiles.total_deductions)} />
                <StatTile Icon={Users} label="Employees paid" value={tiles.employees_paid} />
                <StatTile
                    Icon={tiles.run_status === "completed" ? CheckCircle2 : Clock}
                    label="Run status"
                    value={tiles.run_status ? RUN_STATUS_LABEL[tiles.run_status] : null}
                    tone={RUN_STATUS_TONE[tiles.run_status] || "default"}
                    hint={tiles.run_status ? undefined : "No run for this month"}
                />
                <StatTile
                    Icon={hasVariance && variance < 0 ? TrendingDown : TrendingUp}
                    label="Vs last month"
                    value={hasVariance ? `${variance > 0 ? "+" : ""}${variance}` : null}
                    suffix={hasVariance ? "%" : undefined}
                    tone={hasVariance && Math.abs(variance) >= 10 ? "warning" : "default"}
                    hint={tiles.previous_period_label ? `Against ${tiles.previous_period_label}` : undefined}
                />
                <StatTile Icon={Layers} label="Supplementary runs" value={tiles.supplementary_runs} />
                <StatTile
                    Icon={FileWarning}
                    label="Unpublished payslips"
                    value={tiles.unpublished_payslips}
                    tone={tiles.unpublished_payslips ? "warning" : "default"}
                    onClick={() => navigate("/payroll/payslips")}
                />
                <StatTile
                    Icon={AlertTriangle}
                    label="Pending corrections"
                    value={tiles.pending_corrections}
                    tone={tiles.pending_corrections ? "danger" : "default"}
                />
            </TileGrid>

            <DashboardSection title={`Net payout — last 12 months to ${data?.period?.label || ""}`}>
                <PayoutTrend trend={data?.trend} loading={loading && !data} />
            </DashboardSection>

            <SectionGrid>
                <DashboardSection title={`Deduction split — ${data?.period?.label || ""}`}>
                    <DistributionBars
                        rows={deductionRows}
                        emptyText="No deductions recorded for this month."
                        colorFor={(row) => DEDUCTION_BAR[row.key] || DEDUCTION_BAR.other}
                        formatValue={rupees}
                    />
                </DashboardSection>

                <DashboardSection title="Cost by department">
                    <DistributionBars
                        rows={departmentRows}
                        emptyText="No payslips for this month."
                        colorFor={() => "bg-sidebar-primary"}
                        formatValue={rupees}
                    />
                </DashboardSection>
            </SectionGrid>

            <DashboardSection title="Runs needing attention">
                <DashboardTable
                    columns={attentionColumns}
                    rows={data?.attention || []}
                    onRowClick={() => navigate("/payroll/payrollprocess")}
                    empty="Every payroll run is complete."
                    minWidth="min-w-[420px]"
                />
            </DashboardSection>
        </DashboardShell>
    );
};

export default PayrollDashboard;
